import {
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack
} from '@mui/material';
import { ContaReceber } from '../../Entity/ContaReceber';
import { useEffect, useState } from 'react';
import { EdBetween, EdNot, EdWhereObject, FindManyReq } from '../../Entity/TypeormTypes';
import {
  formatarData,
  formatarDinheiro,
  formatarFatura,
  getIsSmallScreen,
  getTipoStatus,
  parseData,
  useUpdateEffect
} from '../../helper';
import { Dayjs } from 'dayjs';
import contaReceberApi from '../../api/apiContaReceber';
import { toast } from 'react-toastify';
import { DUPR_STATUS_EM_ABERTO, DUPR_STATUS_PAGO, DUPR_STATUS_TODOS } from '../../constants';
import {
  DadosListagemContasReceberCliente,
  ParamsKeyContaReceber
} from '../../types/ContaReceberTypes';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import PrintIcon from '@mui/icons-material/Print';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ModalConfirmarBoleto from './ModalConfirmarPagamento';
import EdTabela from '../../Generic/EdTabela/EdTabela';
import Coluna from '../../Generic/EdTabela/Coluna';
import CardContaReceberMobile from './CardContaReceberMobile';

type Props = {
  codigoStatus: string | null;
  dataInicio: Dayjs;
  dataFim: Dayjs;
  style?: React.CSSProperties;
};

const LINHAS_POR_PAGINA = 10;

const ListaContaReceber: React.FC<Props> = (props) => {
  const { codigoStatus, dataInicio, dataFim } = props;
  const [query, setQuery] = useState<FindManyReq<ContaReceber>>({});
  const [contasReceber, setContasReceber] = useState<DadosListagemContasReceberCliente[]>([]);
  const [totalLinhas, setTotalLinhas] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sessao] = useSessao();
  const [dadosSelecionados, setDadosSelecionados] =
    useState<DadosListagemContasReceberCliente | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [linhaSelecionada, setLinhaSelecionada] =
    useState<DadosListagemContasReceberCliente | null>(null);
  const [openModalConfimarBoleto, setOpenModalConfimarBoleto] = useState(true);

  useEffect(() => {
    buscar();
  }, [codigoStatus, dataInicio, dataFim]);

  useUpdateEffect(() => {
    getContasReceber();
  }, [query, pagina]);

  const buscar = async () => {
    if (!dataInicio || !dataInicio.isValid() || !dataFim || !dataFim.isValid()) {
      return;
    }

    let where: EdWhereObject<ContaReceber> = { cancelada: EdNot('S') };

    // QUANDO SELECIONADO PENDENTE BUSCO TODOS EXCETO OS PAGOS, INDEPENDENTE DA DATA
    if (codigoStatus === DUPR_STATUS_EM_ABERTO) {
      where = {
        ...where,
        statusPagamento: EdNot(DUPR_STATUS_PAGO)
      };
    }

    if (codigoStatus === DUPR_STATUS_PAGO) {
      where = {
        ...where,
        statusPagamento: DUPR_STATUS_PAGO,
        dataVencimento: EdBetween(dataInicio.format('YYYYMMDD'), dataFim.format('YYYYMMDD'))
      };
    }

    if (codigoStatus === DUPR_STATUS_TODOS) {
      where = {
        ...where,
        dataVencimento: EdBetween(dataInicio.format('YYYYMMDD'), dataFim.format('YYYYMMDD'))
      };
    }

    where.tipoDocumento = EdNot('V');
    where.cancelada = ' ';

    const query: FindManyReq<ContaReceber> = {
      where: where,
      relations: ['cliente', 'empresa'],
      order: { dataVencimento: 'ASC' }
    };

    try {
      setLoading(true);
      setQuery(query);
      const totalLinhas = await contaReceberApi.count(query);
      setTotalLinhas(totalLinhas);
      setPagina(0);
    } catch (error: any) {
      setLoading(false);
      console.error('error', error);
      toast.error(error.data || error.message);
    }
  };

  const getContasReceber = async () => {
    try {
      setLoading(true);
      query.skip = pagina * LINHAS_POR_PAGINA;
      query.take = LINHAS_POR_PAGINA;

      const res = await contaReceberApi.getMany(query);
      setContasReceber(res);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error('error', error.data);
      toast.error(error.data || error.message);
    }
  };

  const gerarRecibo = async (contaReceber: DadosListagemContasReceberCliente) => {
    try {
      setLoading(true);

      if (!contaReceber) return;

      const params: ParamsKeyContaReceber = {
        codigoEmpresa: contaReceber.codigoEmpresa,
        codigoCliente: contaReceber.codigoCliente,
        fatura: contaReceber.fatura,
        financeira: contaReceber.financeira
      };

      await contaReceberApi.gerarReciboBaixa(params);
    } catch (error: any) {
      toast.error('Erro ao gerar recibo.');
    } finally {
      setLoading(false);
    }
  };

  const cabecalhoTabela = [
    new Coluna<DadosListagemContasReceberCliente>({
      label: 'Empresa',
      render: (contaReceber) => {
        if (!contaReceber.codigoEmpresa) return '';
        return (
          <p>
            {contaReceber.codigoEmpresa} - {contaReceber.empresa?.nome}
          </p>
        );
      },
      align: 'left'
    }),
    new Coluna<DadosListagemContasReceberCliente>({
      label: 'Fatura',
      render: (contaReceber) => {
        if (getIsSmallScreen()) {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Typography
                variant="body1"
                style={{
                  fontWeight: 'bold',
                  color: 'black'
                }}
              >
                {formatarFatura(contaReceber.fatura)}
              </Typography>
            </div>
          );
        } else {
          return formatarFatura(contaReceber.fatura);
        }
      },
      align: 'center'
    }),
    new Coluna<DadosListagemContasReceberCliente>({
      label: 'Dt. Venc.',
      render: (contaReceber) => formatarData(contaReceber.vencimento),
      align: 'center'
    }),
    new Coluna<DadosListagemContasReceberCliente>({
      label: 'Parcela',
      render: (contaReceber) => {
        if (!contaReceber.parcela) return '';
        return <p>{contaReceber.parcela}º Parcela</p>;
      },
      align: 'center'
    }),
    new Coluna<DadosListagemContasReceberCliente>({
      label: '(R$) Valor',
      render: (contaReceber) => {
        if (contaReceber.status === DUPR_STATUS_PAGO) {
          return (
            <div>
              <div>{formatarDinheiro(contaReceber.valores.valorJaPago)}</div>
            </div>
          );
        }

        return (
          <div>
            <div>{formatarDinheiro(contaReceber.valores.restante)}</div>
          </div>
        );
      },
      align: 'right'
    }),
    new Coluna<DadosListagemContasReceberCliente>({
      label: 'Status',
      render: (contaReceber) => getTipoStatus(contaReceber),
      align: 'center'
    }),
    new Coluna<DadosListagemContasReceberCliente>({
      label: 'Opções',
      render: (contaReceber) => {
        const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
          setLinhaSelecionada(contaReceber);
        };

        return (
          <IconButton size="small" onClick={handleOpen}>
            <MoreVertIcon />
          </IconButton>
        );
      },
      align: 'center'
    })
  ];

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12}>
        {/* Visualização para mobile */}
        <Grid
          item
          xs={12}
          sx={{ display: { xs: 'flex', md: 'none' } }}
          style={{ flexDirection: 'column' }}
        >
          {loading ? (
            <Grid item xs={12}>
              <Typography fontWeight={'bold'} variant="h6">
                Buscando informações ...
              </Typography>
              <LinearProgress />
            </Grid>
          ) : (
            <>
              {contasReceber.length === 0 && (
                <Typography variant="body2">Nenhum débito encontrado no período.</Typography>
              )}

              {contasReceber.map((contaReceber) => (
                <CardContaReceberMobile
                  contaReceber={contaReceber}
                  key={`${contaReceber.codigoEmpresa}-${contaReceber.codigoCliente}-${contaReceber.fatura}-${contaReceber.financeira}`}
                  onSetLoading={(loading: boolean) => {
                    setLoading(loading);
                  }}
                  onSetDadosSelecionados={(
                    contaReceber: DadosListagemContasReceberCliente | null
                  ) => {
                    setDadosSelecionados(contaReceber);
                  }}
                  onSetOpenModalConfimarBoleto={() => setOpenModalConfimarBoleto(true)}
                />
              ))}
            </>
          )}
        </Grid>

        {/* Visualização para desktop */}
        <Grid item xs={12} sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => {
              setAnchorEl(null);
              setDadosSelecionados(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                minWidth: 220,
                borderRadius: 2
              }
            }}
          >
            {/* PAGAMENTO */}
            {linhaSelecionada &&
              linhaSelecionada.status !== DUPR_STATUS_PAGO &&
              !linhaSelecionada.aguardandoConciliacao &&
              (sessao.empresa?.temIntegracaoBoleto || sessao.empresa?.temIntegracaoPix) && (
                <MenuItem
                  dense
                  onClick={() => {
                    setDadosSelecionados(linhaSelecionada);
                    setOpenModalConfimarBoleto(true);
                  }}
                >
                  <ListItemIcon>
                    <ReceiptLongIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Pagar agora" />
                </MenuItem>
              )}

            {/* Recibo */}
            {linhaSelecionada && linhaSelecionada.status !== DUPR_STATUS_EM_ABERTO && (
              <>
                <Divider />
                <MenuItem
                  dense
                  onClick={() => {
                    gerarRecibo(linhaSelecionada);
                    setAnchorEl(null);
                  }}
                >
                  <ListItemIcon>
                    <PrintIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Imprimir recibo" secondary="Documento de pagamento" />
                </MenuItem>
              </>
            )}
          </Menu>
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ display: { xs: 'none', md: 'flex' } }}>
        <EdTabela
          chave={'TabelaEdClienteContaReceber'}
          totalLinhas={totalLinhas}
          pagina={pagina}
          cabecalhoTabela={cabecalhoTabela}
          linhas={contasReceber}
          loading={loading}
          linhasPorPagina={Math.min(totalLinhas, LINHAS_POR_PAGINA)}
          mudarPagina={setPagina}
          onRowClick={(obj) => {
            setLinhaSelecionada(obj);
          }}
          compactar
        />
      </Grid>

      {openModalConfimarBoleto && dadosSelecionados && (
        <ModalConfirmarBoleto
          open={openModalConfimarBoleto}
          onClose={() => {
            setDadosSelecionados(null);
            setOpenModalConfimarBoleto(false);
          }}
          contaReceber={dadosSelecionados}
          onGerarPix={() => {}}
          onGerarBoleto={() => {}}
        />
      )}
    </Grid>
  );
};

export default ListaContaReceber;
